import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { MusterHinweis, musterZuVorlage, useMuster, musterVerantwortung } from "../MusterHinweis";
import { Link, useNavigate, useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CONTRACT_ROLES, compileBlocks, validateBlocks, type Block, type ContractRole, type CustomVariableDef, type FieldRef, type Inline, type Language, type VariableType } from "@talentkontor/domain";
import { api } from "../../../../data";
import { useStaffWorkspace } from "../../../../app/session";
import { ErrorState, Loading, NoAccess, describeError } from "../../../../components/States";
import { Dialog, PageHead, useAction } from "../../../../components/ui";
import { t, nameIn } from "../../../../lib/sprache";
import { activateTemplate, loadContractSettings, loadOrgDaten, loadVorlage, loadVorlagen, newTemplateVersion, type TemplateRow } from "../daten";
import { VertragPapier } from "../Papier";
import { VorschauDialog, useVorschauParam, vorlageQuelle } from "../VertragVorschau";
import { exampleValues, findingText, renderDraft, vorlagenBeispiel } from "../pruefen";
import { ROLE_LABEL } from "../modell";
import { useUngespeichert, VerwerfenDialog } from "../Ungespeichert";
import { TextFeld, type ChipEinfuegen } from "./TextFeld";
import {
  aendere, alleIds, baukastenBefundText, containerVon, erlaubtAn, bausteinMenue, bedingungen, bedingungSatz, behebe, dupliziere, entferne, feldArt, feldKatalog, feldLabel,
  filtereFelder, filtereMenue, finde, fuegeEin, fuegeInContainer, genutzteFelder, gliederung, loeseKlausel, neuerBaustein, neuerPunkt, neuesFeld,
  pruefe, sichtbarkeitText, speicherFelder, standardFilter, verschiebe, wandleUm, type ChipLabel, type FeldEintrag, type MenuePunkt, type NeuerTyp,
  type PruefPunkt,
} from "./modell";
import { andereSprache, genutzteAngaben, kategorieLabel, KATEGORIEN, klauselAktualisieren, klauselEinfuegen, klauselFaehig, klauselInhalt, klauselZu, loadKlauseln, pruefwerte, speichereKlausel, uebersetzungPruefen, type KlauselZeile } from "./bibliothek";
import { schluesselAus } from "./modell";
import { UNTERSCHIED_TEXT, vergleicheUmwandlung } from "./uebernahme";
import "./baukasten.css";

type Ansicht = "bearbeiten" | ContractRole;
type Ziel = { art: "nach"; id: string | null } | { art: "in"; id: string; zweig: 0 | 1 } | { art: "ersetze"; id: string };

/** Route /agentur/vertraege/vorlagen/:templateId/baukasten */
export function Baukasten() {
  const { templateId = "" } = useParams();
  const ws = useStaffWorkspace();
  const orgId = ws.org.org_id;
  const tpl = useQuery({ queryKey: ["contract_templates", orgId, templateId], queryFn: () => loadVorlage(api, orgId, templateId) });
  const list = useQuery({ queryKey: ["contract_templates", orgId], queryFn: () => loadVorlagen(api, orgId) });
  if (tpl.error || list.error) return <ErrorState error={tpl.error ?? list.error} />;
  if (tpl.isPending || list.isPending) return <Loading />;
  if (!tpl.data) return <NoAccess text={t("Diese Vorlage gibt es nicht oder sie ist für dein Konto nicht freigegeben.", "This template does not exist or is not available to your account.")} />;
  return <Editor key={tpl.data.id} vorlage={tpl.data as TemplateRow & { blocks?: Block[] | null }} alle={list.data.templates} />;
}

function Editor({ vorlage, alle }: { vorlage: TemplateRow & { blocks?: Block[] | null }; alle: TemplateRow[] }) {
  const ws = useStaffWorkspace();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const darf = ws.can("edit_templates");
  const editable = darf && vorlage.status === "draft";
  const ausMuster = vorlage.platform_version != null;
  const [geprueft, setGeprueft] = useState(false);
  const lang: Language = vorlage.language;
  const settings = useQuery({ queryKey: ["contract_settings", ws.org.org_id], queryFn: () => loadContractSettings(api, ws.org.org_id) });
  const org = useQuery({ queryKey: ["org_daten", ws.org.org_id], queryFn: () => loadOrgDaten(api, ws.org.org_id) });
  // Stand beim Öffnen: bleibt für die ganze Sitzung (nach dem ersten Speichern trägt die Vorlage Bausteine, die Umwandlung gilt weiter)
  const [start] = useState(() => (vorlage.blocks ? { blocks: vorlage.blocks, freitext: 0, umgewandelt: false } : { ...wandleUm(vorlage), umgewandelt: true }));
  const [blocks, setBlocksRaw] = useState<Block[]>(start.blocks);
  const [defs, setDefs] = useState<CustomVariableDef[]>(vorlage.variables ?? []);
  const [name, setName] = useState(vorlage.name);
  const muster = useMuster();
  const [auswahl, setAuswahl] = useState<string | null>(null);
  const [ansicht, setAnsicht] = useState<Ansicht>("bearbeiten");
  const [menue, setMenue] = useState<Ziel | null>(null);
  const klauseln = useQuery({ queryKey: ["contract_clauses", ws.org.org_id], queryFn: () => loadKlauseln(api, ws.org.org_id) });
  const [klauselZiel, setKlauselZiel] = useState<Ziel | null>(null);
  const [alsKlausel, setAlsKlausel] = useState<Block | null>(null);
  const [feldWunsch, setFeldWunsch] = useState<{ einfuegen: (ref: FieldRef, typ: VariableType) => void } | null>(null);
  // Umgewandelter Entwurf: gleich als Bausteine speichern, sonst veröffentlichte „Veröffentlichen“ die Textkopie ohne Bausteine (Befund 3 Schritt 8)
  const [stand, setStand] = useState<"gespeichert" | "ungespeichert" | "speichert" | "konflikt" | "fehler" | "unvollstaendig">(start.umgewandelt && editable ? "ungespeichert" : "gespeichert");
  const startDefs = useRef(defs);
  const [fehler, setFehler] = useState<string | null>(null);
  const zeit = useRef(vorlage.updated_at);
  const verlauf = useRef<Block[][]>([]);
  const zukunft = useRef<Block[][]>([]);
  const { busy, error, run, attempt } = useAction();
  const { blocker, allow } = useUngespeichert(stand !== "gespeichert");
  // K-511: Vorschau als Vollansicht (Seiten wie im PDF, Beispielwerte, PDF und Druck mit Entwurfsvermerk)
  const vorschauParam = useVorschauParam();
  const laeuft = useRef<Promise<boolean> | null>(null);

  const setBlocks = useCallback((next: Block[] | ((b: Block[]) => Block[])) => {
    setBlocksRaw((prev) => {
      const v = typeof next === "function" ? next(prev) : next;
      if (v === prev) return prev;
      verlauf.current = [...verlauf.current.slice(-49), prev];
      zukunft.current = [];
      return v;
    });
    setStand("ungespeichert");
  }, []);
  const rueckgaengig = () => {
    const prev = verlauf.current.pop();
    if (!prev) return;
    setBlocksRaw((cur) => { zukunft.current.push(cur); return prev; });
    setStand("ungespeichert");
  };
  const wiederholen = () => {
    const next = zukunft.current.pop();
    if (!next) return;
    setBlocksRaw((cur) => { verlauf.current.push(cur); return next; });
    setStand("ungespeichert");
  };
  const touchDefs = (d: CustomVariableDef[]) => { setDefs(d); setStand("ungespeichert"); };

  const felder = useMemo(() => speicherFelder(blocks, defs, lang), [blocks, defs, lang]);
  const pruefung = useMemo(() => pruefe(blocks, defs, lang), [blocks, defs, lang]);
  const chip: ChipLabel = useCallback((n) => {
    if (n.t === "if") return { text: t(`Nur wenn ${bedingungSatz(n.when, defs)}`, `Only if ${bedingungSatz(n.when, defs)}`), art: "wenn" };
    const art = feldArt(n.field, defs);
    const d = n.field.src === "custom" ? defs.find((x) => x.key === n.field.path) : undefined;
    const zusatz = art === "unterzeichner" && d?.fillable_by ? t(` · füllt ${ROLE_LABEL[d.fillable_by]} aus`, ` · filled in by ${ROLE_LABEL[d.fillable_by]}`) : "";
    return { text: feldLabel(n.field, defs) + zusatz, art };
  }, [defs]);

  // Automatisch speichern (Entwurf), mit Versionsbedingung gegen stilles Überschreiben (VB-27)
  useEffect(() => {
    if (!editable || (stand !== "ungespeichert" && stand !== "fehler")) return;
    // nach einem Fehler (etwa Netz) erneut nach 10 Sekunden
    const h = setTimeout(() => { void speichern(); }, stand === "fehler" ? 10000 : 1200);
    return () => clearTimeout(h);
  });
  /** Speichern nacheinander: ein zweiter Aufruf wartet auf den laufenden (sonst träfe er mit altem updated_at einen Konflikt). */
  async function speichern(): Promise<boolean> {
    return nacheinander(speichernJetzt);
  }
  /** Schreibvorgänge nacheinander (Speichern, Abgleich des Sprachpaars): keiner trifft mit altem updated_at (Befund 11 Schritt 4) */
  async function nacheinander(fn: () => Promise<boolean>): Promise<boolean> {
    while (laeuft.current) await laeuft.current;
    const p = fn();
    laeuft.current = p;
    try { return await p; } finally { if (laeuft.current === p) laeuft.current = null; }
  }
  async function speichernJetzt(): Promise<boolean> {
    if (!editable) return true;
    // Unvollständige Bausteine (etwa leerer Container) lehnt die Datenbank ab: erst speichern, wenn sie vollständig sind
    if (validateBlocks(blocks) !== null) { setStand("unvollstaendig"); return false; }
    setStand("speichert");
    try {
      const rows = await api.update<{ id: string; updated_at: string }>("contract_templates", [["id", "eq", vorlage.id], ["updated_at", "eq", zeit.current]],
        { ...felder, name: name.trim() || vorlage.name }, { columns: "id,updated_at" });
      if (!rows.length) { setStand("konflikt"); return false; }
      zeit.current = rows[0]!.updated_at;
      setStand((s) => (s === "speichert" ? "gespeichert" : s));
      setFehler(null);
      return true;
    } catch (e) {
      const d = describeError(e);
      setFehler(`${d.title} ${d.text}`.trim());
      setStand("fehler");
      return false;
    }
  }

  const zielContainer = (z: Ziel): string | null => (z.art === "in" ? z.id : z.id === null ? null : containerVon(blocks, z.id));
  const ziehe = (z: Ziel, typ: NeuerTyp) => {
    if (typ === "private" && blocks.some((x) => x.type === "private")) { setMenue(null); return; }
    const ids = alleIds(blocks, defs);
    setzeEin(z, neuerBaustein(typ, ids, lang, felder.required_signers.length ? felder.required_signers : ["agency", "talent"]), erlaubtAn(blocks, typ, zielContainer(z)));
  };
  const setzeEin = (z: Ziel, neu: Block, erlaubt: boolean) => {
    setBlocks((b) => {
      // An dieser Stelle nicht erlaubt (etwa vertraulicher Teil in einem „Nur wenn“): oben einfügen, der leere Absatz bleibt
      if (!erlaubt) return fuegeEin(b, null, neu);
      return z.art === "nach" ? fuegeEin(b, z.id, neu) : z.art === "in" ? fuegeInContainer(b, z.id, z.zweig, neu) : aendere(b, z.id, () => neu);
    });
    setMenue(null);
    // Schreibmarke in den neuen Baustein; gewählt bleibt der Baustein selbst (auch bei Containern)
    setTimeout(() => { document.querySelector<HTMLElement>(`#bk-${neu.id} .bk-text`)?.focus(); setAuswahl(neu.id); }, 60);
  };

  const onTaste = (e: KeyboardEvent<HTMLDivElement>) => {
    const mod = e.metaKey || e.ctrlKey;
    const inText = (e.target as HTMLElement).isContentEditable;
    if (mod && e.key.toLowerCase() === "z" && !inText) { e.preventDefault(); if (e.shiftKey) wiederholen(); else rueckgaengig(); return; }
    if (!auswahl || !editable) return;
    if (e.altKey && (e.key === "ArrowUp" || e.key === "ArrowDown")) { e.preventDefault(); setBlocks((b) => verschiebe(b, auswahl, e.key === "ArrowUp" ? -1 : 1)); return; }
    if (mod && e.key.toLowerCase() === "d") { e.preventDefault(); setBlocks((b) => dupliziere(b, auswahl)); return; }
    // ↑/↓ auf einem gewählten Baustein ohne Text (Feldzeile, Seitenumbruch, Unterschriften) wechseln zum Nachbarn
    if (!inText && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
      const alle = Array.from(document.querySelectorAll<HTMLElement>(".bk-papier [data-block]"));
      const i = alle.findIndex((x) => x.dataset.block === auswahl);
      const ziel = alle[i + (e.key === "ArrowUp" ? -1 : 1)];
      if (ziel) { e.preventDefault(); ziel.focus(); setAuswahl(ziel.dataset.block!); }
      return;
    }
    if (e.key === "Escape") { (e.target as HTMLElement).closest<HTMLElement>("[data-block]")?.focus(); }
  };

  const version = alle.find((x) => x.key === vorlage.key && x.language === vorlage.language && x.status === "active");
  const entwurfDanach = alle.find((x) => x.key === vorlage.key && x.language === vorlage.language && x.status === "draft" && x.id !== vorlage.id);
  const { law, ...beispiel } = vorlagenBeispiel(org.data, lang, settings.data?.number_format ?? "V-{NNNN}", new Date().getFullYear());
  const vorschau = ansicht === "bearbeiten" ? null : renderDraft({ body: felder.body_html, annex: felder.annex_html, defs: felder.variables, values: exampleValues(felder.variables), ...beispiel },
    ansicht, felder.annex_hidden_from, felder.required_signers);
  void law;
  const [vergleichOffen, setVergleichOffen] = useState(false);
  // Nach einer Änderung vergleicht der Hinweis nicht mehr (die Vorschau zeigt dann den neuen Stand, Befund 4 Schritt 8)
  const unveraendert = blocks === start.blocks && defs === startDefs.current;
  const vergleich = useMemo(() => (start.umgewandelt && settings.isSuccess && org.isSuccess ? vergleicheUmwandlung(vorlage, start.blocks, beispiel) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nur der Stand beim Öffnen wird verglichen
    [start, settings.isSuccess, org.isSuccess]);

  const ctx: Ctx = {
    blocks, defs, lang, editable, chip, auswahl, klauseln: klauseln.data ?? [], klauselnGeladen: klauseln.isSuccess,
    setDefs: (d) => touchDefs(d),
    waehle: setAuswahl, aendern: (id, fn) => setBlocks((b) => aendere(b, id, fn)),
    menue: setMenue, feld: (cb) => setFeldWunsch({ einfuegen: cb }),
    // Enter: neuer Absatz direkt dahinter, Schreibmarke hinein
    absatzNach: (id) => {
      const neu = neuerBaustein("paragraph", alleIds(blocks, defs), lang);
      setBlocks((b) => fuegeEin(b, id, neu));
      setTimeout(() => { document.querySelector<HTMLElement>(`#bk-${neu.id} .bk-text`)?.focus(); setAuswahl(neu.id); }, 30);
    },
  };

  return (
    <main id="main" className="content bk" onKeyDown={onTaste}>
      <PageHead title={name || vorlage.name} eyebrow={t(`Vorlage bauen · ${vorlage.status === "draft" ? "Entwurf" : vorlage.status === "active" ? "aktiv" : "archiviert"} · Version ${vorlage.version}`, `Build template · ${vorlage.status === "draft" ? "draft" : vorlage.status === "active" ? "active" : "archived"} · version ${vorlage.version}`)}
        text={version && version.id !== vorlage.id ? t(`Aktiv ist Version ${version.version}. Sie bleibt, bis du diese Version veröffentlichst.`, `Version ${version.version} is active. It stays until you publish this version.`) : undefined}>
        <Link className="btn" to={`/agentur/vertraege/vorlagen/${vorlage.id}`}>{t("Bisherige Ansicht", "Classic view")}</Link>
        <button type="button" onClick={() => vorschauParam.open(vorlage.id)}>{t("Vorschau", "Preview")}</button>
        {darf && vorlage.status !== "draft" && (entwurfDanach
          ? <Link className="btn primary" to={`/agentur/vertraege/vorlagen/${entwurfDanach.id}/baukasten`}>{t(`Entwurf Version ${entwurfDanach.version} öffnen`, `Open draft version ${entwurfDanach.version}`)}</Link>
          : <button className="primary" disabled={!!busy} onClick={async () => {
            const id = await run("version", () => newTemplateVersion(api, vorlage.id));
            if (id) navigate(`/agentur/vertraege/vorlagen/${id}/baukasten`);
          }}>{t("Bearbeiten (neue Version)", "Edit (new version)")}</button>)}
        {editable && (
          <button className="primary" disabled={!!busy || pruefung.sperrt || stand === "konflikt" || stand === "unvollstaendig" || (ausMuster && !geprueft)} title={pruefung.sperrt ? t("Erst die sperrenden Punkte der Prüfung beheben.", "First fix the blocking points of the check.") : undefined}
            onClick={async () => {
              if (stand !== "gespeichert" && !(await speichern())) return;
              if (await attempt("veroeffentlichen", () => activateTemplate(api, vorlage.id), t("Veröffentlicht. Neue Verträge nutzen jetzt diese Version.", "Published. New contracts now use this version."))) { allow(); navigate("/agentur/vertraege/vorlagen"); }
            }}>{t("Veröffentlichen", "Publish")}</button>
        )}
      </PageHead>
      <VorschauDialog open={vorschauParam.id === vorlage.id} onClose={vorschauParam.close} title={t(`Vorschau: ${name || vorlage.name}`, `Preview: ${name || vorlage.name}`)}
        quelle={vorschauParam.id === vorlage.id ? vorlageQuelle({ name: name || vorlage.name, language: lang, body_html: felder.body_html, annex_html: felder.annex_html, variables: felder.variables,
          annex_hidden_from: felder.annex_hidden_from, required_signers: felder.required_signers }, org.data) : null} />
      {/* 0181 (Anwalt Punkt 13): aus einem CreatorOffice-Muster übernommen */}
      <MusterHinweis zeile={musterZuVorlage(muster.zeilen, vorlage)} version={vorlage.platform_version} kompakt />
      {/* Aus einem CreatorOffice-Muster: vor dem Veröffentlichen die Eigenverantwortung bestätigen (AGB „Leistungen“) */}
      {editable && ausMuster && (
        <label className="row notice"><input type="checkbox" checked={geprueft} onChange={(e) => setGeprueft(e.target.checked)} />
          {musterVerantwortung()} {t("Erst dann lässt sie sich veröffentlichen.", "Only then can it be published.")}</label>
      )}

      <div className="toolbar bk-leiste">
        <span role="group" aria-label={t("Ansicht", "View")} className="bk-umschalter">
          <button type="button" aria-pressed={ansicht === "bearbeiten"} onClick={() => setAnsicht("bearbeiten")}>{editable ? t("Bearbeiten", "Edit") : t("Dokument", "Document")}</button>
          {(["agency", "brand", "talent"] as ContractRole[]).map((r) => (
            <button key={r} type="button" aria-pressed={ansicht === r} onClick={() => setAnsicht(r)}>{t(`Vorschau als ${ROLE_LABEL[r]}`, `Preview as ${ROLE_LABEL[r]}`)}</button>
          ))}
        </span>
        {editable && <span className={`pill ${stand === "konflikt" || stand === "fehler" ? "error" : stand === "unvollstaendig" ? "warning" : "neutral"}`} role="status">{STAND_TEXT[stand]()}</span>}
        {editable && stand === "fehler" && <button type="button" onClick={() => void speichern()}>{t("Erneut speichern", "Save again")}</button>}
        {editable && (
          <span className="bk-undo">
            <button type="button" onClick={rueckgaengig} disabled={!verlauf.current.length} aria-label={t("Rückgängig", "Undo")}>↶</button>
            <button type="button" onClick={wiederholen} disabled={!zukunft.current.length} aria-label={t("Wiederholen", "Redo")}>↷</button>
          </span>
        )}
      </div>
      {start.umgewandelt && (
        <p className="notice" role="note">{editable
          ? t("Diese Vorlage war bisher Text mit Platzhaltern. Sie ist hier in Bausteine umgewandelt und wird als Entwurf in Bausteinen gespeichert; die aktive Version bleibt, bis du veröffentlichst.", "This template used to be text with placeholders. It has been converted into blocks here and is saved as a draft in blocks; the active version stays until you publish.")
          : t("Diese Vorlage ist Text mit Platzhaltern. Hier siehst du sie in Bausteinen; gespeichert wird nichts.", "This template is text with placeholders. Here you see it in blocks; nothing is saved.")}
          {start.freitext > 0 && t(` ${start.freitext} Stelle(n) ließen sich nicht zuordnen und stehen als „Freitext aus alter Vorlage“ im Dokument.`, ` ${start.freitext} spot(s) could not be mapped and remain as “free text from the old template”.`)}
          {vergleich && unveraendert && (
            <span className="bk-vergleich">
              {" "}<span className={`pill ${vergleich.ok ? "success" : "warning"}`} role="status"><span className="pill-zeichen">{vergleich.ok ? "✓" : "◷"}</span> {vergleich.ok
                ? t("Vorschau vorher und nachher gleich", "Preview before and after identical")
                : !vergleich.gleich
                  ? t(`Vorschau weicht ab: ${vergleich.rollen.filter((r) => !r.gleich).map((r) => ROLE_LABEL[r.rolle]).join(", ")}`, `Preview differs: ${vergleich.rollen.filter((r) => !r.gleich).map((r) => ROLE_LABEL[r.rolle]).join(", ")}`)
                  : t(`Vorschau gleich, aber die Umwandlung ändert: ${vergleich.unterschiede.map((u) => UNTERSCHIED_TEXT[u]()).join(", ")}`, `Preview identical, but the conversion changes: ${vergleich.unterschiede.map((u) => UNTERSCHIED_TEXT[u]()).join(", ")}`)}</span>
              {" "}<button type="button" onClick={() => setVergleichOffen(true)}>{t("Vorher und nachher vergleichen", "Compare before and after")}</button>
            </span>
          )}
          {vergleich && !unveraendert && t(" Du hast die umgewandelte Vorlage geändert; die Vorschau zeigt den neuen Stand.", " You have changed the converted template; the preview shows the new version.")}</p>
      )}
      {vergleich && (
        <Dialog open={vergleichOffen} onClose={() => setVergleichOffen(false)} title={t("Vorher und nachher", "Before and after")} wide>
          <p>{vergleich.quelleGleich
            ? t("Text, vertraulicher Teil, Angaben und Parteien sind Zeichen für Zeichen gleich. Verträge aus der neuen Version sehen aus wie bisher.", "Text, confidential part, fields and parties are identical character for character. Contracts from the new version look as before.")
            : t(`Die Umwandlung ändert die gespeicherte Vorlage (${vergleich.unterschiede.map((u) => UNTERSCHIED_TEXT[u]()).join(", ")}). Bitte die Prüfung und die Vorschau je Rolle ansehen, bevor du veröffentlichst.`, `The conversion changes the stored template (${vergleich.unterschiede.map((u) => UNTERSCHIED_TEXT[u]()).join(", ")}). Please look at the check and the preview for each role before you publish.`)}</p>
          {vergleich.rollen.map((r) => (
            <section key={r.rolle} className="stack" aria-label={ROLE_LABEL[r.rolle]}>
              <h3>{t(`Ansicht ${ROLE_LABEL[r.rolle]}`, `View ${ROLE_LABEL[r.rolle]}`)} <span className={`pill ${r.gleich ? "success" : "warning"}`}>{r.gleich ? t("gleich", "identical") : t("weicht ab", "differs")}</span></h3>
              {!r.gleich && (
                <div className="bk-vergleich-spalten">
                  <VertragPapier logoPath={ws.org.logo_path} accent={ws.org.brand_color} html={r.vorher.html} annexHtml={r.vorher.annexHtml} lang={lang} eyebrow={t("Vorher", "Before")} marks={[]} />
                  <VertragPapier logoPath={ws.org.logo_path} accent={ws.org.brand_color} html={r.nachher.html} annexHtml={r.nachher.annexHtml} lang={lang} eyebrow={t("Nachher", "After")} marks={[]} />
                </div>
              )}
            </section>
          ))}
        </Dialog>
      )}
      {!darf && <p className="notice">{t("Vorlagen bearbeiten Admins und Teammitglieder mit dem Recht „Vorlagen bearbeiten“. Du siehst die Vorlage zum Nachlesen.", "Admins and team members with the “Edit templates” permission edit templates. You see the template for reference.")}</p>}
      {stand === "konflikt" && (
        <p className="notice warning" role="alert">{t("Die Vorlage wurde inzwischen an anderer Stelle geändert. Deine Änderungen sind nicht gespeichert. ", "The template has meanwhile been changed elsewhere. Your changes are not saved. ")}
          <button type="button" onClick={() => { allow(); void qc.invalidateQueries({ queryKey: ["contract_templates"] }).then(() => navigate(0)); }}>{t("Neu laden", "Reload")}</button></p>
      )}
      {(fehler || error) && <p className="tk-error" role="alert">{fehler ?? error}</p>}

      <div className="bk-raster">
        <aside className="bk-links" aria-label={t("Gliederung und Bausteine", "Outline and blocks")}>
          <section className="panel stack">
            <h3 className="bk-titel">{t("Gliederung", "Outline")}</h3>
            <ol className="bk-gliederung">
              {gliederung(blocks, defs).map((g) => (
                <li key={g.id} style={{ paddingLeft: `${g.tiefe * 12}px` }}>
                  <button type="button" className={auswahl === g.id ? "aktiv" : ""} onClick={() => { setAuswahl(g.id); setAnsicht("bearbeiten"); document.getElementById(`bk-${g.id}`)?.scrollIntoView({ block: "center", behavior: "smooth" }); }}>
                    <span>{g.titel}</span>{g.merkmal && <small className="pill neutral">{g.merkmal}</small>}
                  </button>
                </li>
              ))}
            </ol>
          </section>
          {editable && (
            <section className="panel stack">
              <h3 className="bk-titel">{t("Bausteine", "Blocks")}</h3>
              <p className="bk-klein">{t("Klick fügt hinter dem gewählten Baustein ein. Im Text: „/“ am Anfang einer leeren Zeile, „{“ für ein Feld.", "Click inserts after the selected block. In the text: “/” at the start of an empty line, “{” for a field.")}</p>
              <ul className="bk-palette">
                {bausteinMenue().map((p) => (
                  <li key={p.typ}><button type="button" disabled={p.typ === "private" && blocks.some((x) => x.type === "private")} onClick={() => ziehe({ art: "nach", id: auswahl && finde(blocks, auswahl) ? auswahl : null }, p.typ)}><span aria-hidden="true" className="bk-zeichen">{p.zeichen}</span>{p.label}</button></li>
                ))}
              </ul>
            </section>
          )}
        </aside>

        <section className="bk-mitte" aria-label={t("Dokument", "Document")}>
          {vorschau ? (
            <VertragPapier logoPath={ws.org.logo_path} accent={ws.org.brand_color} html={vorschau.html} annexHtml={vorschau.annexHtml} defs={felder.variables} lang={lang}
              eyebrow={t(`Vorschau als ${ROLE_LABEL[ansicht as ContractRole]} · Beispielwerte`, `Preview as ${ROLE_LABEL[ansicht as ContractRole]} · sample values`)}
              marks={CONTRACT_ROLES.map((r) => ({ role: r, signed: false }))} />
          ) : (
            <div className="paper bk-papier">
              <label className="bk-name">{t("Name der Vorlage", "Template name")}
                <input value={name} disabled={!editable} onChange={(e) => { setName(e.target.value); setStand("ungespeichert"); }} />
              </label>
              <Liste bloecke={blocks} ctx={ctx} />
              {editable && <button type="button" className="bk-plus" onClick={() => setMenue({ art: "nach", id: null })}>{t("+ Baustein", "+ Block")}</button>}
            </div>
          )}
        </section>

        <aside className="bk-rechts" aria-label={t("Prüfung und Eigenschaften", "Check and properties")}>
          <Pruefung punkte={pruefung.punkte} defs={defs} editable={editable} onBehebe={(p) => {
            if (p.behebung?.art === "zeigen") { const id = p.behebung.baustein; setAuswahl(id); setAnsicht("bearbeiten"); document.getElementById(`bk-${id}`)?.scrollIntoView({ block: "center", behavior: "smooth" }); return; }
            setBlocks((b) => behebe(b, p, lang));
          }} />
          {auswahl && finde(blocks, auswahl) && (
            <Eigenschaften block={finde(blocks, auswahl)!} ctx={ctx} onAlsKlausel={darf ? () => setAlsKlausel(finde(blocks, auswahl)) : undefined} onEntfernen={() => { setBlocks((b) => entferne(b, auswahl)); setAuswahl(null); }}
              onVerschieben={(r) => setBlocks((b) => verschiebe(b, auswahl, r))} onDuplizieren={() => setBlocks((b) => dupliziere(b, auswahl))}
              onLoesen={() => setBlocks((b) => loeseKlausel(b, auswahl))} />
          )}
          <Sprachpaar vorlage={vorlage} alle={alle} blocks={blocks} name={name} darf={darf} lang={lang} defs={defs}
            zeit={zeit} nacheinander={nacheinander} onFehler={(m) => setFehler(m)} />
          <section className="panel stack">
            <h3 className="bk-titel">{t("Tastatur", "Keyboard")}</h3>
            <ul className="bk-klein bk-tasten">
              <li><kbd>/</kbd> {t("Baustein (in leerer Zeile)", "block (in an empty line)")}</li>
              <li><kbd>Enter</kbd> {t("neuer Absatz", "new paragraph")}</li>
              <li><kbd>{"{"}</kbd> {t("Feld einsetzen", "insert field")}</li>
              <li><kbd>Alt</kbd>+<kbd>↑</kbd>/<kbd>↓</kbd> {t("Baustein verschieben", "move block")}</li>
              <li><kbd>{MOD()}</kbd>+<kbd>D</kbd> {t("duplizieren", "duplicate")}</li>
              <li><kbd>{MOD()}</kbd>+<kbd>B</kbd>/<kbd>I</kbd>/<kbd>U</kbd> {t("fett, kursiv, unterstrichen", "bold, italic, underline")}</li>
              <li><kbd>{MOD()}</kbd>+<kbd>Z</kbd> {t("rückgängig (außerhalb des Texts: Bausteine)", "undo (outside the text: blocks)")}</li>
              <li><kbd>↑</kbd>/<kbd>↓</kbd> {t("am Rand: nächster Baustein", "at the edge: next block")}</li>
              <li><kbd>⇧</kbd>+<kbd>Enter</kbd> {t("Zeilenumbruch", "line break")}</li>
            </ul>
          </section>
        </aside>
      </div>

      <BausteinMenue onKlausel={() => { setKlauselZiel(menue); setMenue(null); }} ziel={menue} erlaubt={(typ) => !(typ === "private" && blocks.some((x) => x.type === "private")) && (!menue || menue.art === "nach" || erlaubtAn(blocks, typ, zielContainer(menue)))} onClose={() => setMenue(null)} onWahl={(p) => menue && ziehe(menue, p.typ)} />
      <FeldAuswahl offen={!!feldWunsch} blocks={blocks} defs={defs} lang={lang} onClose={() => setFeldWunsch(null)}
        onWahl={(f) => { feldWunsch?.einfuegen(f.ref, f.typ); setFeldWunsch(null); }}
        onNeu={(d) => { touchDefs([...defs, d]); feldWunsch?.einfuegen({ src: "custom", path: d.key }, d.type); setFeldWunsch(null); }} />
      <KlauselAuswahl ziel={klauselZiel} liste={klauseln.data ?? []} lang={lang} onClose={() => setKlauselZiel(null)}
        onWahl={(k) => { const z = klauselZiel!; setKlauselZiel(null); const r = klauselEinfuegen(k, blocks, defs); if (r.defs.length !== defs.length) touchDefs(r.defs); setzeEin(z, r.block, true); }} />
      <AlsKlausel block={alsKlausel} lang={lang} orgId={ws.org.org_id} liste={klauseln.data ?? []} defs={defs} onClose={() => setAlsKlausel(null)} onDone={() => { setAlsKlausel(null); void klauseln.refetch(); }} />
      <VerwerfenDialog blocker={blocker} />
    </main>
  );
}

const MOD = () => (typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘" : t("Strg", "Ctrl"));

const STAND_TEXT: Record<"gespeichert" | "ungespeichert" | "speichert" | "konflikt" | "fehler" | "unvollstaendig", () => string> = {
  unvollstaendig: () => t("◷ unvollständig, noch nicht gespeichert (siehe Prüfung)", "◷ incomplete, not saved yet (see check)"),
  gespeichert: () => t("✓ gespeichert", "✓ saved"),
  ungespeichert: () => t("○ Änderungen werden gespeichert …", "○ changes are being saved …"),
  speichert: () => t("○ speichert …", "○ saving …"),
  konflikt: () => t("! nicht gespeichert (Konflikt)", "! not saved (conflict)"),
  fehler: () => t("! nicht gespeichert", "! not saved"),
};

interface Ctx {
  blocks: Block[];
  defs: CustomVariableDef[];
  lang: Language;
  editable: boolean;
  chip: ChipLabel;
  auswahl: string | null;
  waehle: (id: string) => void;
  aendern: (id: string, fn: (b: Block) => Block) => void;
  menue: (z: Ziel) => void;
  feld: (cb: (ref: FieldRef, typ: VariableType) => void) => void;
  absatzNach: (id: string) => void;
  klauseln: KlauselZeile[];
  klauselnGeladen: boolean;
  setDefs: (d: CustomVariableDef[]) => void;
}

function Liste({ bloecke, ctx }: { bloecke: readonly Block[]; ctx: Ctx }) {
  return <>{bloecke.map((b) => <BausteinAnsicht key={b.id} b={b} ctx={ctx} />)}</>;
}

/** Text eines Bausteins mit „/“, „{“ und Enter */
function Text({ b, value, onChange, label, className, placeholder, ctx }: { b: Block; value: Inline[]; onChange: (v: Inline[]) => void; label: string; className?: string; placeholder?: string; ctx: Ctx }) {
  const feld = (einfuegen: ChipEinfuegen) => ctx.feld((ref, typ) => {
    const filters = standardFilter(typ);
    einfuegen(filters ? { t: "field", field: ref, filters } : { t: "field", field: ref });
  });
  return (
    <TextFeld value={value} onChange={onChange} label={label} chip={ctx.chip} className={className} placeholder={placeholder} readOnly={!ctx.editable}
      onFocus={() => ctx.waehle(b.id)} onSlash={() => ctx.menue({ art: "ersetze", id: b.id })} onEnter={() => ctx.absatzNach(b.id)} onFeld={feld} />
  );
}

function Rahmen({ b, ctx, kopf, children, klasse }: { b: Block; ctx: Ctx; kopf?: ReactNode; children: ReactNode; klasse?: string }) {
  // Bausteine ohne eigenes Textfeld sind selbst per Tab erreichbar (Enter/Leertaste wählt sie)
  const ohneText = b.type === "field" || b.type === "pagebreak" || b.type === "signatures" || b.type === "legacy_html" || b.type === "clause";
  return (
    <div id={`bk-${b.id}`} data-block={b.id} tabIndex={ohneText ? 0 : -1} role={ohneText ? "button" : undefined}
      aria-label={ohneText ? t("Baustein wählen", "Select block") : undefined} aria-pressed={ohneText ? ctx.auswahl === b.id : undefined}
      className={`bk-block ${klasse ?? ""} ${ctx.auswahl === b.id ? "gewaehlt" : ""}`}
      onFocus={(e) => { if (e.target === e.currentTarget) ctx.waehle(b.id); }}
      onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); ctx.waehle(b.id); } }}
      onClick={(e) => { e.stopPropagation(); ctx.waehle(b.id); }}>
      {kopf && <div className="bk-kopf">{kopf}</div>}
      {children}
    </div>
  );
}

function Container({ b, ctx, kinder, zweig = 0 }: { b: Block; ctx: Ctx; kinder: readonly Block[]; zweig?: 0 | 1 }) {
  return (
    <div className="bk-kinder">
      <Liste bloecke={kinder} ctx={ctx} />
      {ctx.editable && <button type="button" className="bk-plus klein" onClick={(e) => { e.stopPropagation(); ctx.menue({ art: "in", id: b.id, zweig }); }}>{t("+ Baustein hier", "+ Block here")}</button>}
    </div>
  );
}

function BausteinAnsicht({ b, ctx }: { b: Block; ctx: Ctx }) {
  const set = (fn: (x: Block) => Block) => ctx.aendern(b.id, fn);
  switch (b.type) {
    case "heading":
      return <Rahmen b={b} ctx={ctx}><Text b={b} ctx={ctx} value={b.text} onChange={(v) => set((x) => ({ ...x, text: v }) as Block)} label={t("Überschrift", "Heading")} className={`bk-h${b.level}`} placeholder={t("Überschrift", "Heading")} /></Rahmen>;
    case "paragraph":
      return <Rahmen b={b} ctx={ctx}><Text b={b} ctx={ctx} value={b.text} onChange={(v) => set((x) => ({ ...x, text: v }) as Block)} label={t("Absatz", "Paragraph")} className={b.align ? `bk-align-${b.align}` : ""} placeholder={t("Text schreiben, „/“ für Bausteine, „{“ für Felder", "Write text, “/” for blocks, “{” for fields")} /></Rahmen>;
    case "note":
      return <Rahmen b={b} ctx={ctx} klasse="bk-notiz" kopf={<span>{t("Interner Hinweis · steht nie im Vertrag", "Internal note · never in the contract")}</span>}><Text b={b} ctx={ctx} value={b.text} onChange={(v) => set((x) => ({ ...x, text: v }) as Block)} label={t("Interner Hinweis", "Internal note")} /></Rahmen>;
    case "list":
      return (
        <Rahmen b={b} ctx={ctx}>
          {(() => {
            const Tag = b.ordered ? "ol" : "ul";
            return (
              <Tag className="bk-liste">
                {b.items.map((it, i) => (
                  <li key={it.id} data-item={it.id}>
                    {it.when && <small className="pill info">{t(`nur wenn ${bedingungSatz(it.when, ctx.defs)}`, `only if ${bedingungSatz(it.when, ctx.defs)}`)}</small>}
                    <TextFeld value={it.text} chip={ctx.chip} readOnly={!ctx.editable} label={t(`Punkt ${i + 1}`, `Item ${i + 1}`)} onFocus={() => ctx.waehle(b.id)}
                      onChange={(v) => set((x) => x.type === "list" ? { ...x, items: x.items.map((y) => (y.id === it.id ? { ...y, text: v } : y)) } : x)}
                      onEnter={() => {
                        const items = neuerPunkt(b.items, it.id, alleIds(ctx.blocks));
                        const neu = items[items.findIndex((y) => y.id === it.id) + 1]!;
                        set((x) => x.type === "list" ? { ...x, items } : x);
                        setTimeout(() => { const el = document.querySelector<HTMLElement>(`#bk-${b.id} [data-item="${neu.id}"] .bk-text`); if (el) el.focus(); }, 30);
                      }}
                      onFeld={(einfuegen) => ctx.feld((ref, typ) => { const f = standardFilter(typ); einfuegen(f ? { t: "field", field: ref, filters: f } : { t: "field", field: ref }); })} />
                    {ctx.editable && b.items.length > 1 && <button type="button" className="bk-x" aria-label={t(`Punkt ${i + 1} entfernen`, `Remove item ${i + 1}`)} onClick={() => set((x) => x.type === "list" ? { ...x, items: x.items.filter((y) => y.id !== it.id) } : x)}>×</button>}
                  </li>
                ))}
              </Tag>
            );
          })()}
          {ctx.editable && <button type="button" className="bk-plus klein" onClick={() => set((x) => x.type === "list" ? { ...x, items: neuerPunkt(x.items, null, alleIds(ctx.blocks)) } : x)}>{t("+ Punkt", "+ Item")}</button>}
        </Rahmen>
      );
    case "table":
      return (
        <Rahmen b={b} ctx={ctx}>
          <table className="bk-tabelle">
            {b.header && <thead><tr>{b.header.map((c, j) => <th key={j}><TextFeld value={c} chip={ctx.chip} readOnly={!ctx.editable} label={t(`Kopfzeile Spalte ${j + 1}`, `Header column ${j + 1}`)} onFocus={() => ctx.waehle(b.id)}
              onChange={(v) => set((x) => x.type === "table" && x.header ? { ...x, header: x.header.map((y, k) => (k === j ? v : y)) } : x)} /></th>)}</tr></thead>}
            <tbody>
              {b.rows.map((r, i) => (
                <tr key={i}>{r.map((c, j) => <td key={j}><TextFeld value={c} chip={ctx.chip} readOnly={!ctx.editable} label={t(`Zeile ${i + 1}, Spalte ${j + 1}`, `Row ${i + 1}, column ${j + 1}`)} onFocus={() => ctx.waehle(b.id)}
                  onChange={(v) => set((x) => x.type === "table" ? { ...x, rows: x.rows.map((row, ri) => (ri === i ? row.map((cell, cj) => (cj === j ? v : cell)) : row)) } : x)}
                  onFeld={(einfuegen) => ctx.feld((ref, typ) => { const f = standardFilter(typ); einfuegen(f ? { t: "field", field: ref, filters: f } : { t: "field", field: ref }); })} /></td>)}</tr>
              ))}
            </tbody>
          </table>
          {ctx.editable && (
            <div className="row bk-klein">
              <button type="button" onClick={() => set((x) => x.type === "table" ? { ...x, rows: [...x.rows, Array.from({ length: spalten(x) }, () => [])] } : x)}>{t("+ Zeile", "+ Row")}</button>
              <button type="button" onClick={() => set((x) => x.type === "table" ? { ...x, header: x.header?.concat([[]]), rows: x.rows.map((r) => [...r, []]) } : x)}>{t("+ Spalte", "+ Column")}</button>
              <button type="button" disabled={b.rows.length < 2} onClick={() => set((x) => x.type === "table" ? { ...x, rows: x.rows.slice(0, -1) } : x)}>{t("− Zeile", "− Row")}</button>
              <button type="button" disabled={spalten(b) < 2} onClick={() => set((x) => x.type === "table" ? { ...x, header: x.header?.slice(0, -1), rows: x.rows.map((r) => r.slice(0, -1)) } : x)}>{t("− Spalte", "− Column")}</button>
            </div>
          )}
        </Rahmen>
      );
    case "field":
      return (
        <Rahmen b={b} ctx={ctx}>
          <p className="bk-feldzeile"><strong>{b.label}:</strong> <span className={`bk-chip bk-chip-${feldArt(b.field, ctx.defs)}`}>{feldLabel(b.field, ctx.defs)}</span></p>
        </Rahmen>
      );
    case "condition":
      return (
        <Rahmen b={b} ctx={ctx} klasse="bk-container bk-wenn" kopf={<><span className="bk-marke">⑂</span> {t(`Nur wenn ${bedingungSatz(b.when, ctx.defs)}`, `Only if ${bedingungSatz(b.when, ctx.defs)}`)}</>}>
          <Container b={b} ctx={ctx} kinder={b.children} />
          {b.otherwise && <><div className="bk-sonst">{t("Sonst", "Otherwise")}</div><Container b={b} ctx={ctx} kinder={b.otherwise} zweig={1} /></>}
        </Rahmen>
      );
    case "choice":
      return (
        <Rahmen b={b} ctx={ctx} klasse="bk-container bk-wahl" kopf={<><span className="bk-marke">☑</span> {t("Wählbare Klausel: ", "Optional clause: ")}<strong>{nameIn(b.question.de, b.question.en) || t("(Frage fehlt)", "(question missing)")}</strong> <small>{b.default ? t("Standard: an", "default: on") : t("Standard: aus", "default: off")}</small></>}>
          <Container b={b} ctx={ctx} kinder={b.children} />
        </Rahmen>
      );
    case "private":
      return (
        <Rahmen b={b} ctx={ctx} klasse="bk-container bk-privat" kopf={<><span className="bk-marke">◐</span> <strong>{b.title || t("Vertraulicher Teil", "Confidential part")}</strong> · {sichtbarkeitText(b.visibleTo)} <small>{t("Steht am Ende des Vertrags.", "Placed at the end of the contract.")}</small></>}>
          <Container b={b} ctx={ctx} kinder={b.children} />
        </Rahmen>
      );
    case "attachment":
      return (
        <Rahmen b={b} ctx={ctx} klasse="bk-container bk-anlage" kopf={<><span className="bk-marke">📎</span> {t("Anlage: ", "Annex: ")}<strong>{b.title}</strong> <small>{t("sehen alle Parteien", "all parties see it")}</small></>}>
          <Container b={b} ctx={ctx} kinder={b.children} />
        </Rahmen>
      );
    case "clause":
      return (
        <Rahmen b={b} ctx={ctx} klasse="bk-container bk-klausel" kopf={(() => {
          const aktuell = klauselZu(ctx.klauseln, b.clauseId, ctx.lang);
          return (
            <>
              <span className="bk-marke">§</span> {aktuell ? <strong>{aktuell.title}</strong> : t("Klausel aus der Bibliothek", "Clause from the library")} · {t(`Version ${b.version}`, `version ${b.version}`)}
              {aktuell && aktuell.version > b.version && (
                <>
                  <span className="pill warning">{t(`neuere Fassung verfügbar (Version ${aktuell.version})`, `newer version available (version ${aktuell.version})`)}</span>
                  {ctx.editable && <button type="button" onClick={(e) => { e.stopPropagation(); const r = klauselAktualisieren(b, aktuell, ctx.blocks, ctx.defs); if (r.defs.length !== ctx.defs.length) ctx.setDefs(r.defs); ctx.aendern(b.id, () => r.block); }}>{t("Aktualisieren", "Update")}</button>}
                </>
              )}
              {!aktuell && ctx.klauselnGeladen && <small>{t("(nicht mehr in der Bibliothek)", "(no longer in the library)")}</small>}
            </>
          );
        })()}>
          <div className="bk-kinder bk-fest"><Liste bloecke={b.children} ctx={{ ...ctx, editable: false }} /></div>
        </Rahmen>
      );
    case "signatures":
      return (
        <Rahmen b={b} ctx={ctx} klasse="bk-unterschriften" kopf={<span>{t("Unterschriften", "Signatures")}</span>}>
          <div className="bk-sig-reihe">{b.parties.map((r) => <div key={r} className="bk-sig"><div className="bk-sig-linie" />{ROLE_LABEL[r]}</div>)}</div>
        </Rahmen>
      );
    case "pagebreak":
      return <Rahmen b={b} ctx={ctx} klasse="bk-umbruch"><span>{t("Seitenumbruch", "Page break")}</span></Rahmen>;
    case "legacy_html":
      return (
        <Rahmen b={b} ctx={ctx} klasse="bk-freitext" kopf={<span>{t("Freitext aus alter Vorlage · nur lesen", "Free text from the old template · read only")}</span>}>
          <pre className="bk-roh">{b.html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()}</pre>
        </Rahmen>
      );
  }
}

function spalten(b: Extract<Block, { type: "table" }>): number {
  return Math.max(b.header?.length ?? 0, ...b.rows.map((r) => r.length), 1);
}

/** Eigenschaften des gewählten Bausteins (rechts). */
function Eigenschaften({ block: b, ctx, onEntfernen, onVerschieben, onDuplizieren, onLoesen, onAlsKlausel }: {
  block: Block; ctx: Ctx; onEntfernen: () => void; onVerschieben: (r: -1 | 1) => void; onDuplizieren: () => void; onLoesen: () => void; onAlsKlausel?: () => void;
}) {
  const set = (fn: (x: Block) => Block) => ctx.aendern(b.id, fn);
  const dis = !ctx.editable;
  const toggle = (list: readonly ContractRole[], r: ContractRole) => CONTRACT_ROLES.filter((x) => (x === r ? !list.includes(r) : list.includes(x)));
  const TYP = bausteinMenue().find((m) => m.typ === b.type)?.label ?? (b.type === "clause" ? t("Klausel", "Clause") : t("Freitext", "Free text"));
  return (
    <section className="panel stack bk-eigenschaften" aria-label={t("Eigenschaften", "Properties")}>
      <h3 className="bk-titel">{t("Eigenschaften", "Properties")} <span className="pill neutral">{TYP}</span></h3>
      {b.type === "heading" && (
        <label>{t("Ebene", "Level")}<select value={b.level} disabled={dis} onChange={(e) => set((x) => ({ ...x, level: Number(e.target.value) as 1 | 2 | 3 | 4 }) as Block)}>
          <option value={1}>{t("Titel", "Title")}</option><option value={2}>{t("Paragraph", "Section")}</option><option value={3}>{t("Unterpunkt", "Subsection")}</option><option value={4}>{t("klein", "small")}</option>
        </select></label>
      )}
      {(b.type === "heading" || b.type === "paragraph") && (
        <label>{t("Ausrichtung", "Alignment")}<select value={b.align ?? ""} disabled={dis} onChange={(e) => set((x) => { const v = e.target.value; const { align: _a, ...rest } = x as { align?: string }; return (v ? { ...rest, align: v } : rest) as Block; })}>
          <option value="">{t("links", "left")}</option><option value="center">{t("zentriert", "centred")}</option><option value="right">{t("rechts", "right")}</option>
        </select></label>
      )}
      {b.type === "list" && <label className="row"><input type="checkbox" checked={b.ordered} disabled={dis} onChange={(e) => set((x) => ({ ...x, ordered: e.target.checked }) as Block)} />{t("nummeriert", "numbered")}</label>}
      {b.type === "table" && <label className="row"><input type="checkbox" checked={!!b.header} disabled={dis} onChange={(e) => set((x) => x.type === "table" ? (e.target.checked ? { ...x, header: Array.from({ length: spalten(x) }, () => []) } : (({ header: _h, ...r }) => r)(x)) as Block : x)} />{t("Kopfzeile", "Header row")}</label>}
      {b.type === "field" && (
        <>
          <label>{t("Bezeichnung", "Label")}<input value={b.label} disabled={dis} maxLength={120} onChange={(e) => set((x) => ({ ...x, label: e.target.value }) as Block)} /></label>
          <p>{t("Feld: ", "Field: ")}<strong>{feldLabel(b.field, ctx.defs)}</strong></p>
          {!dis && <button type="button" onClick={() => ctx.feld((ref, typ) => set((x) => { const f = standardFilter(typ); const { filters: _f, ...rest } = x as Extract<Block, { type: "field" }>; return (f ? { ...rest, field: ref, filters: f } : { ...rest, field: ref }) as Block; }))}>{t("Anderes Feld wählen", "Choose another field")}</button>}
        </>
      )}
      {b.type === "condition" && (
        <>
          <label>{t("Nur wenn …", "Only if …")}<select value={`${b.when.src}:${b.when.path}`} disabled={dis} onChange={(e) => { const [src, path] = e.target.value.split(":") as ["system" | "custom", string]; set((x) => ({ ...x, when: { src, path } }) as Block); }}>
            {bedingungen(ctx.defs).map((c) => <option key={`${c.ref.src}:${c.ref.path}`} value={`${c.ref.src}:${c.ref.path}`}>{c.satz}</option>)}
            {!bedingungen(ctx.defs).some((c) => c.ref.src === b.when.src && c.ref.path === b.when.path) && <option value={`${b.when.src}:${b.when.path}`}>{bedingungSatz(b.when, ctx.defs)}</option>}
          </select></label>
          <label className="row"><input type="checkbox" checked={!!b.otherwise} disabled={dis} onChange={(e) => set((x) => x.type === "condition" ? (e.target.checked ? { ...x, otherwise: [neuerBaustein("paragraph", alleIds(ctx.blocks), ctx.lang)] } : (({ otherwise: _o, wsElse: _w, ...r }) => r)(x)) as Block : x)} />{t("mit „Sonst“", "with “otherwise”")}</label>
        </>
      )}
      {b.type === "choice" && (
        <>
          <label>{t("Frage beim Erstellen (Deutsch)", "Question when creating (German)")}<input value={b.question.de} disabled={dis} maxLength={120} onChange={(e) => set((x) => x.type === "choice" ? { ...x, question: { ...x.question, de: e.target.value } } : x)} /></label>
          <label>{t("Frage beim Erstellen (Englisch)", "Question when creating (English)")}<input value={b.question.en} disabled={dis} maxLength={120} onChange={(e) => set((x) => x.type === "choice" ? { ...x, question: { ...x.question, en: e.target.value } } : x)} /></label>
          <label>{t("Erklärung (optional)", "Explanation (optional)")}<input value={b.help?.de ?? ""} disabled={dis} maxLength={500} onChange={(e) => set((x) => x.type === "choice" ? (e.target.value ? { ...x, help: { de: e.target.value, en: x.help?.en ?? "" } } : (({ help: _h, ...r }) => r)(x)) as Block : x)} /></label>
          <label className="row"><input type="checkbox" checked={b.default} disabled={dis} onChange={(e) => set((x) => ({ ...x, default: e.target.checked }) as Block)} />{t("Standard: an", "Default: on")}</label>
        </>
      )}
      {b.type === "private" && (
        <>
          <label>{t("Interner Titel (nur im Editor, steht nicht im Vertrag)", "Internal title (editor only, not in the contract)")}<input value={b.title ?? ""} disabled={dis} maxLength={120} onChange={(e) => set((x) => x.type === "private" ? (e.target.value ? { ...x, title: e.target.value } : (({ title: _t, ...r }) => r)(x)) as Block : x)} /></label>
          <fieldset className="stack"><legend>{t("Sichtbar für", "Visible to")}</legend>
            {CONTRACT_ROLES.map((r) => <label key={r} className="row"><input type="checkbox" checked={b.visibleTo.includes(r)} disabled={dis || r === "agency"} onChange={() => set((x) => x.type === "private" ? { ...x, visibleTo: toggle(x.visibleTo, r) } : x)} />{ROLE_LABEL[r]}{r === "agency" ? t(" (immer)", " (always)") : ""}</label>)}
          </fieldset>
          <small>{sichtbarkeitText(b.visibleTo)} {t("Die Kopfzeile steht beim Versand über dem Teil, wird mitgedruckt und gehasht.", "When sending, the header appears above the part; it is printed and hashed.")}</small>
        </>
      )}
      {b.type === "attachment" && <label>{t("Titel der Anlage", "Annex title")}<input value={b.title} disabled={dis} maxLength={120} onChange={(e) => set((x) => ({ ...x, title: e.target.value }) as Block)} /></label>}
      {b.type === "signatures" && (
        <fieldset className="stack"><legend>{t("Wer unterschreibt", "Who signs")}</legend>
          {CONTRACT_ROLES.map((r) => <label key={r} className="row"><input type="checkbox" checked={b.parties.includes(r)} disabled={dis || (b.parties.length === 1 && b.parties.includes(r))} onChange={() => set((x) => x.type === "signatures" ? { ...x, parties: toggle(x.parties, r) } : x)} />{ROLE_LABEL[r]}</label>)}
          <small>{t("Mindestens eine Partei unterschreibt.", "At least one party signs.")}</small>
        </fieldset>
      )}
      {b.type === "clause" && !dis && <button type="button" onClick={onLoesen}>{t("Aus der Bibliothek lösen (frei bearbeiten)", "Detach from the library (edit freely)")}</button>}
      {onAlsKlausel && klauselFaehig(klauselInhalt(b)) && <button type="button" onClick={onAlsKlausel}>{t("Als Klausel speichern", "Save as clause")}</button>}
      {!dis && (
        <div className="row bk-werkzeug">
          <button type="button" onClick={() => onVerschieben(-1)} aria-label={t("nach oben", "up")}>↑</button>
          <button type="button" onClick={() => onVerschieben(1)} aria-label={t("nach unten", "down")}>↓</button>
          {b.type !== "private" && b.type !== "signatures" && <button type="button" onClick={onDuplizieren}>{t("Duplizieren", "Duplicate")}</button>}
          <button type="button" className="danger" onClick={onEntfernen}>{t("Entfernen", "Remove")}</button>
        </div>
      )}
    </section>
  );
}

function Pruefung({ punkte, defs, editable, onBehebe }: { punkte: PruefPunkt[]; defs: CustomVariableDef[]; editable: boolean; onBehebe: (p: PruefPunkt) => void }) {
  const sperrend = punkte.filter((p) => p.sperrt).length;
  return (
    <section className="panel stack bk-pruefung" aria-label={t("Prüfung", "Check")}>
      <h3 className="bk-titel">{t("Prüfung", "Check")} {sperrend > 0 ? <span className="pill error">{t(`${sperrend} sperrt`, `${sperrend} blocking`)}</span> : <span className="pill success"><span className="pill-zeichen">✓</span> {t("bereit", "ready")}</span>}</h3>
      {punkte.length === 0 && <p className="bk-klein">{t("Keine Hinweise. Die Vorlage lässt sich veröffentlichen.", "No notes. The template can be published.")}</p>}
      <ul className="bk-punkte">
        {punkte.map((p, i) => (
          <li key={i} className={p.sperrt ? "sperrt" : p.finding.severity === "warning" ? "pruefen" : "hinweis"}>
            <strong>{p.sperrt ? t("Sperrt das Veröffentlichen", "Blocks publishing") : p.finding.severity === "warning" ? t("Bitte prüfen", "Please check") : t("Hinweis", "Note")}</strong>
            <span>{baukastenBefundText(p.finding, defs) ?? findingText(p.finding, defs)}</span>
            {(editable || p.behebung?.art === "zeigen") && p.behebung && <button type="button" onClick={() => onBehebe(p)}>{p.behebung.art === "bedingt" ? t("Satz nur zeigen, wenn ausgefüllt", "Only show the sentence if filled in") : p.behebung.art === "zeigen" ? t("Zur Stelle", "Go to the spot") : t("+ Einfügen", "+ Insert")}</button>}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** „/“-Menü mit Suche, Pfeiltasten und Enter */
function BausteinMenue({ ziel, erlaubt, onClose, onWahl, onKlausel }: { ziel: Ziel | null; erlaubt: (typ: NeuerTyp) => boolean; onClose: () => void; onWahl: (p: MenuePunkt) => void; onKlausel: () => void }) {
  const [suche, setSuche] = useState("");
  const [i, setI] = useState(0);
  const eingabe = useRef<HTMLInputElement>(null);
  // showModal setzt den Fokus auf „Schließen“; die Suche soll ihn haben
  useEffect(() => { setSuche(""); setI(0); if (ziel) setTimeout(() => eingabe.current?.focus(), 0); }, [ziel]);
  const punkte = filtereMenue(bausteinMenue(), suche).filter((p) => erlaubt(p.typ));
  return (
    <Dialog open={!!ziel} onClose={onClose} title={t("Baustein einfügen", "Insert block")}>
      <input ref={eingabe} aria-label={t("Baustein suchen", "Search block")} role="combobox" aria-expanded="true" aria-controls="bk-menue-liste"
        aria-activedescendant={punkte[i] ? `bk-menue-${punkte[i]!.typ}` : undefined} placeholder={t("Suchen …", "Search …")} value={suche}
        onChange={(e) => { setSuche(e.target.value); setI(0); }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") { e.preventDefault(); setI((x) => Math.min(x + 1, punkte.length - 1)); }
          if (e.key === "ArrowUp") { e.preventDefault(); setI((x) => Math.max(x - 1, 0)); }
          if (e.key === "Enter" && punkte[i]) { e.preventDefault(); onWahl(punkte[i]!); }
        }} />
      <ul className="bk-menue" role="listbox" id="bk-menue-liste" aria-label={t("Bausteine", "Blocks")}>
        {punkte.map((p, k) => (
          <li key={p.typ} id={`bk-menue-${p.typ}`} role="option" aria-selected={k === i}>
            <button type="button" className={k === i ? "aktiv" : ""} onMouseEnter={() => setI(k)} onClick={() => onWahl(p)}>
              <span className="bk-zeichen" aria-hidden="true">{p.zeichen}</span><span><strong>{p.label}</strong><small>{p.hilfe}</small></span>
            </button>
          </li>
        ))}
        {!punkte.length && <li className="bk-klein">{t("Kein Baustein passt.", "No block matches.")}</li>}
        <li><button type="button" onClick={onKlausel}><span className="bk-zeichen" aria-hidden="true">§</span><span><strong>{t("Klausel aus der Bibliothek …", "Clause from the library …")}</strong><small>{t("wiederverwendbare Klausel einfügen", "insert a reusable clause")}</small></span></button></li>
      </ul>
      <p className="bk-klein">{t("↑/↓ wählen · Enter einfügen · Esc schließen", "↑/↓ choose · Enter insert · Esc close")}</p>
    </Dialog>
  );
}

/** Feldauswahl über „{“: schon genutzt, automatisch nach Herkunft, beim Erstellen, vom Unterzeichner, „+ Neues Feld“ */
function FeldAuswahl({ offen, blocks, defs, lang, onClose, onWahl, onNeu }: {
  offen: boolean; blocks: Block[]; defs: CustomVariableDef[]; lang: Language; onClose: () => void; onWahl: (f: FeldEintrag) => void; onNeu: (d: CustomVariableDef) => void;
}) {
  const [suche, setSuche] = useState("");
  const [neu, setNeu] = useState(false);
  const [label, setLabel] = useState("");
  const [typ, setTyp] = useState<VariableType>("text");
  const [wer, setWer] = useState<"agency_create" | ContractRole>("agency_create");
  const [pflicht, setPflicht] = useState(true);
  const suchfeld = useRef<HTMLInputElement>(null);
  useEffect(() => { if (offen) { setSuche(""); setNeu(false); setLabel(""); setTyp("text"); setWer("agency_create"); setPflicht(true); setTimeout(() => suchfeld.current?.focus(), 0); } }, [offen]);
  const katalog = feldKatalog(defs);
  const treffer = filtereFelder(katalog, suche);
  const genutzt = suche ? [] : genutzteFelder(blocks).map((r) => katalog.find((k) => k.ref.src === r.src && k.ref.path === r.path)).filter((x): x is FeldEintrag => !!x);
  const gruppen = new Map<string, FeldEintrag[]>();
  for (const f of treffer) {
    const g = f.art === "system" ? t(`Automatisch · ${f.gruppe}`, `Automatic · ${f.gruppe}`) : f.gruppe;
    gruppen.set(g, [...(gruppen.get(g) ?? []), f]);
  }
  const ARTEN: [VariableType, string][] = [["text", t("Text", "Text")], ["lines", t("mehrzeilig", "multi-line")], ["money", t("Betrag", "Amount")], ["date", t("Datum", "Date")], ["number", t("Zahl", "Number")], ["percent", t("Prozent", "Percent")], ["boolean", t("Ja/Nein", "Yes/No")]];
  const knopf = (f: FeldEintrag) => (
    <li key={`${f.ref.src}:${f.ref.path}`}><button type="button" onClick={() => onWahl(f)}><span className={`bk-chip bk-chip-${f.art}`}>{f.label}</span>{f.beispiel && <small>{t("z. B. ", "e.g. ")}{f.beispiel}</small>}</button></li>
  );
  return (
    <Dialog open={offen} onClose={onClose} title={neu ? t("Neues Feld", "New field") : t("Feld einsetzen", "Insert field")} wide>
      {!neu ? (
        <div className="stack">
          <div className="row">
            <input ref={suchfeld} aria-label={t("Feld suchen", "Search field")} placeholder={t("Suchen, etwa „Budget“ oder „Anschrift“", "Search, e.g. “budget” or “address”")} value={suche} onChange={(e) => setSuche(e.target.value)} />
            <button type="button" className="primary" onClick={() => { setNeu(true); setLabel(suche); }}>{t("+ Neues Feld", "+ New field")}</button>
          </div>
          <div className="bk-feldliste">
            {genutzt.length > 0 && <><h4>{t("Schon genutzt", "Already used")}</h4><ul>{genutzt.map(knopf)}</ul></>}
            {[...gruppen.entries()].map(([g, fs]) => <div key={g}><h4>{g}</h4><ul>{fs.map(knopf)}</ul></div>)}
            {!treffer.length && <p className="bk-klein">{t("Kein Feld passt. Mit „+ Neues Feld“ legst du eins an.", "No field matches. Create one with “+ New field”.")}</p>}
          </div>
        </div>
      ) : (
        <form className="stack" onSubmit={(e) => { e.preventDefault(); if (label.trim().length >= 2) onNeu(neuesFeld({ label, typ, wer, pflicht }, defs, lang)); }}>
          <label>{t("Bezeichnung", "Label")}<input autoFocus required minLength={2} maxLength={120} value={label} onChange={(e) => setLabel(e.target.value)} placeholder={t("etwa „Veröffentlichung bis“", "e.g. “Publish by”")} /></label>
          <label>{t("Art", "Type")}<select value={typ} onChange={(e) => setTyp(e.target.value as VariableType)}>{ARTEN.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
          <label>{t("Wer füllt aus", "Who fills it in")}<select value={wer} onChange={(e) => setWer(e.target.value as "agency_create" | ContractRole)}>
            <option value="agency_create">{t("Agentur beim Erstellen", "Agency when creating")}</option>
            <option value="talent">{t("Creator beim Unterschreiben", "Creator when signing")}</option>
            <option value="brand">{t("Marke beim Unterschreiben", "Brand when signing")}</option>
          </select></label>
          <label className="row"><input type="checkbox" checked={pflicht} onChange={(e) => setPflicht(e.target.checked)} />{t("Pflicht", "Required")}</label>
          <div className="row">
            <button type="submit" className="primary">{t("Anlegen und einsetzen", "Create and insert")}</button>
            <button type="button" onClick={() => setNeu(false)}>{t("Zurück", "Back")}</button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

/** Klausel aus der Bibliothek wählen (Sprache der Vorlage, nach Kategorie) */
function KlauselAuswahl({ ziel, liste, lang, onClose, onWahl }: { ziel: Ziel | null; liste: KlauselZeile[]; lang: Language; onClose: () => void; onWahl: (k: KlauselZeile) => void }) {
  const [suche, setSuche] = useState("");
  const feld = useRef<HTMLInputElement>(null);
  useEffect(() => { if (ziel) { setSuche(""); setTimeout(() => feld.current?.focus(), 0); } }, [ziel]);
  const q = suche.trim().toLowerCase();
  const rows = liste.filter((k) => k.language === lang && !k.archived_at && (!q || k.title.toLowerCase().includes(q) || kategorieLabel(k.category).toLowerCase().includes(q)));
  return (
    <Dialog open={!!ziel} onClose={onClose} title={t("Klausel aus der Bibliothek", "Clause from the library")} wide>
      <input ref={feld} aria-label={t("Klausel suchen", "Search clause")} placeholder={t("Suchen …", "Search …")} value={suche} onChange={(e) => setSuche(e.target.value)} />
      <div className="bk-feldliste">
        {KATEGORIEN.map((c) => {
          const g = rows.filter((k) => k.category === c);
          if (!g.length) return null;
          return (
            <div key={c}>
              <h4>{kategorieLabel(c)}</h4>
              <ul>{g.map((k) => <li key={k.clauseId}><button type="button" onClick={() => onWahl(k)}><span className={`bk-chip ${k.eigen ? "bk-chip-erstellen" : "bk-chip-system"}`}>{k.title}</span><small>{k.eigen ? t("eigene", "own") : "CreatorOffice"} · {t(`Version ${k.version}`, `version ${k.version}`)}</small></button></li>)}</ul>
            </div>
          );
        })}
        {!rows.length && <p className="bk-klein">{t("Keine Klausel in dieser Sprache.", "No clause in this language.")}</p>}
      </div>
      <p className="bk-klein"><Link to="/agentur/vertraege/klauseln">{t("Zur Klausel-Bibliothek", "To the clause library")}</Link></p>
    </Dialog>
  );
}

/** „Als Klausel speichern“: gleicher Titel (Schlüssel) → neue Version der eigenen Klausel */
function AlsKlausel({ block, lang, orgId, liste, defs, onClose, onDone }: { block: Block | null; lang: Language; orgId: string; liste: KlauselZeile[]; defs: CustomVariableDef[]; onClose: () => void; onDone: () => void }) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState<string>("other");
  const { busy, error, attempt } = useAction();
  useEffect(() => {
    if (!block) return;
    const eigen = block.type === "clause" ? liste.find((k) => k.eigen && k.clauseId === block.clauseId && k.language === lang) : undefined;
    const kopf = klauselInhalt(block).find((x) => x.type === "heading");
    setTitle(eigen?.title ?? (kopf && kopf.type === "heading" ? kopf.text.map((n) => (n.t === "text" ? n.v : "")).join("").replace(/^§\s*\d+\s*/, "").trim() : ""));
    setCategory(eigen?.category ?? "other");
  }, [block, liste, lang]);
  const key = block?.type === "clause" && liste.some((k) => k.eigen && k.clauseId === block.clauseId) ? block.clauseId : schluesselAus(title, new Set());
  const vorhanden = liste.find((k) => k.eigen && k.key === key && k.language === lang);
  return (
    <Dialog open={!!block} onClose={onClose} title={t("Als Klausel speichern", "Save as clause")}>
      {block && (
        <form className="stack" onSubmit={async (e) => {
          e.preventDefault();
          if (await attempt("klausel", () => speichereKlausel(api, orgId, { key, language: lang, title: title.trim(), category, blocks: klauselInhalt(block), variables: genutzteAngaben(klauselInhalt(block), defs) }),
            vorhanden ? t(`Klausel gespeichert (neue Version ${vorhanden.version + 1}).`, `Clause saved (new version ${vorhanden.version + 1}).`) : t("Klausel in der Bibliothek gespeichert.", "Clause saved in the library."))) onDone();
        }}>
          <label>{t("Titel", "Title")}<input required minLength={2} maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)} /></label>
          <label>{t("Kategorie", "Category")}<select value={category} onChange={(e) => setCategory(e.target.value)}>{KATEGORIEN.map((c) => <option key={c} value={c}>{kategorieLabel(c)}</option>)}</select></label>
          <p className="bk-klein">{vorhanden ? t(`Eine eigene Klausel mit diesem Titel gibt es schon (Version ${vorhanden.version}); gespeichert wird eine neue Version. Vorlagen, die sie nutzen, zeigen dann „neuere Fassung verfügbar“.`, `An own clause with this title already exists (version ${vorhanden.version}); a new version is saved. Templates using it then show “newer version available”.`)
            : t(`Sprache: ${lang === "de" ? "Deutsch" : "Englisch"}. Die Klausel steht danach in der Bibliothek und über „/“ in jedem Editor.`, `Language: ${lang === "de" ? "German" : "English"}. The clause is then in the library and available via “/” in every editor.`)}</p>
          {error && <p className="tk-error" role="alert">{error}</p>}
          <div className="row"><button className="primary" type="submit" disabled={!!busy || title.trim().length < 2}>{t("Speichern", "Save")}</button><button type="button" onClick={onClose}>{t("Abbrechen", "Cancel")}</button></div>
        </form>
      )}
    </Dialog>
  );
}

/**
 * Sprachpaar (3.5, VB-14): Deutsch und Englisch derselben Kennung. Zeigt, welche Bausteine der anderen Sprache sich seit dem
 * letzten Abgleich geändert haben („Übersetzung prüfen“); legt die andere Fassung mit denselben Baustein-Kennungen an.
 */
function Sprachpaar({ vorlage, alle, blocks, name, darf, lang, defs, zeit, nacheinander, onFehler }: {
  vorlage: TemplateRow & { blocks?: Block[] | null; translation_source?: Record<string, string> | null };
  alle: TemplateRow[]; blocks: Block[]; name: string; darf: boolean; lang: Language; defs: CustomVariableDef[];
  zeit: { current: string }; nacheinander: (fn: () => Promise<boolean>) => Promise<boolean>; onFehler: (m: string) => void;
}) {
  const navigate = useNavigate();
  const ws = useStaffWorkspace();
  const andere = andereSprache(lang);
  const fam = alle.filter((x) => x.key === vorlage.key && x.language === andere && x.status !== "archived").sort((a, b) => b.version - a.version);
  const partner = fam[0];
  const pq = useQuery({ queryKey: ["contract_templates", ws.org.org_id, partner?.id ?? "-"], queryFn: () => (partner ? loadVorlage(api, ws.org.org_id, partner.id) : Promise.resolve(null)), enabled: !!partner });
  const [stand, setStand] = useState<Record<string, string> | null>(vorlage.translation_source ?? null);
  const { busy, run, attempt } = useAction();
  const sprache = andere === "en" ? t("Englische Fassung", "English version") : t("Deutsche Fassung", "German version");
  const pb = (pq.data as (TemplateRow & { blocks?: Block[] | null }) | null | undefined)?.blocks ?? null;
  const pruef = pb ? uebersetzungPruefen(pb, stand) : null;
  const titelVon = (id: string) => {
    const x = pb ? finde(pb, id) : null;
    if (!x) return id;
    const txt = x.type === "heading" || x.type === "paragraph" || x.type === "note" ? x.text.map((n) => (n.t === "text" ? n.v : "…")).join("").slice(0, 60) : "";
    return txt || bausteinMenue().find((m) => m.typ === x.type)?.label || x.type;
  };
  return (
    <section className="panel stack" aria-label={t("Sprachpaar", "Language pair")}>
      <h3 className="bk-titel">{t("Sprachpaar", "Language pair")} <span className="pill neutral">{lang.toUpperCase()} · {andere.toUpperCase()}</span></h3>
      {!partner && (
        <>
          <p className="bk-klein">{t(`${sprache} gibt es noch nicht.`, `${sprache} does not exist yet.`)}</p>
          {darf && (
            <button type="button" disabled={!!busy} onClick={async () => {
              const c = compileBlocks(blocks, { language: andere, variables: defs });
              const rows = await run("sprachpaar", () => api.insert<{ id: string }>("contract_templates", {
                org_id: vorlage.org_id, key: vorlage.key, name: `${name.trim() || vorlage.name} (${andere.toUpperCase()})`, language: andere, kind_id: vorlage.kind_id, status: "draft",
                // Version nach allen Fassungen dieser Sprache (auch archivierten); Herkunft aus der Galerie bleibt erhalten
                version: Math.max(0, ...alle.filter((x) => x.key === vorlage.key && x.language === andere).map((x) => x.version)) + 1,
                platform_version: vorlage.platform_version,
                blocks, blocks_schema: 1, body_html: c.bodyHtml, annex_html: c.annexHtml, annex_hidden_from: c.annexHiddenFrom, variables: c.variables, required_signers: c.requiredSigners,
                // noch nicht übersetzt: kein Abgleich, bis jemand die Übersetzung bestätigt (Befund 10)
                description: vorlage.description, translation_source: null,
              }, { columns: "id" }), t("Fassung angelegt. Bitte den Text übersetzen; die Bausteine sind verknüpft.", "Version created. Please translate the text; the blocks are linked."));
              if (rows?.[0]) navigate(`/agentur/vertraege/vorlagen/${rows[0].id}/baukasten`);
            }}>{andere === "en" ? t("Englische Fassung anlegen", "Create English version") : t("Deutsche Fassung anlegen", "Create German version")}</button>
          )}
        </>
      )}
      {partner && (
        <>
          <p className="bk-klein"><Link to={`/agentur/vertraege/vorlagen/${partner.id}/baukasten`}>{sprache}</Link> · {t(`Version ${partner.version}`, `version ${partner.version}`)}</p>
          {partner && !pq.isPending && !pb && <p className="bk-klein">{t("Die andere Fassung ist noch nicht in Bausteinen. Öffne sie einmal im Baukasten.", "The other version is not in blocks yet. Open it once in the builder.")}</p>}
          {pb && !stand && <p className="bk-klein">{t("Noch nicht abgeglichen.", "Not aligned yet.")}</p>}
          {pruef && pruef.geaendert.length + pruef.neu.length === 0 && <p><span className="pill success"><span className="pill-zeichen">✓</span> {t("Übersetzung aktuell", "Translation up to date")}</span></p>}
          {pruef && pruef.geaendert.length + pruef.neu.length > 0 && (
            <>
              <p><span className="pill warning">{t(`Übersetzung prüfen: ${pruef.geaendert.length + pruef.neu.length} Baustein(e)`, `Check translation: ${pruef.geaendert.length + pruef.neu.length} block(s)`)}</span></p>
              <ul className="bk-klein">
                {pruef.geaendert.slice(0, 8).map((id) => <li key={id}>{t("geändert: ", "changed: ")}{titelVon(id)}</li>)}
                {pruef.neu.slice(0, 8).map((id) => <li key={id}>{t("neu: ", "new: ")}{titelVon(id)}</li>)}
              </ul>
            </>
          )}
          {darf && pb && (!pruef || pruef.geaendert.length + pruef.neu.length > 0) && (
            <button type="button" disabled={!!busy} onClick={async () => {
              const werte = pruefwerte(pb);
              const ok = await nacheinander(() => attempt("abgleich", async () => {
                const rows = await api.update<{ id: string; updated_at: string }>("contract_templates", [["id", "eq", vorlage.id], ["updated_at", "eq", zeit.current]], { translation_source: werte }, { columns: "id,updated_at" });
                if (!rows.length) throw new Error(t("Die Vorlage wurde inzwischen geändert. Bitte neu laden.", "The template has meanwhile been changed. Please reload."));
                zeit.current = rows[0]!.updated_at;
              }, t("Abgleich bestätigt.", "Alignment confirmed.")));
              if (ok) setStand(werte); else onFehler(t("Abgleich nicht gespeichert.", "Alignment not saved."));
            }}>{t("Übersetzung geprüft, Abgleich bestätigen", "Translation checked, confirm alignment")}</button>
          )}
        </>
      )}
    </section>
  );
}
